import {
  CURSOR_ANIMATOR_ID,
  CURSOR_ICON_ID,
  ICON_CLASS_MUSIC_NOTE,
  ICON_CLASS_PLAY,
} from "./constants";
import * as THREE from "three";
import type { AudioManager } from "./AudioManager";

export class InteractionManager {
  private camera: THREE.PerspectiveCamera;
  private scene: THREE.Scene;
  private audioManager: AudioManager;
  private cursorElement: HTMLElement | null;
  private cursorAnimatorElement: HTMLElement | null;
  private cursorIconElement: HTMLElement | null = null;
  private targetMouseX: number = 0;
  private targetMouseY: number = 0;
  private lastKnownMouseX: number = window.innerWidth / 2;
  private lastKnownMouseY: number = window.innerHeight / 2;
  public normalizedMouseX: number = 0.5;
  public normalizedMouseY: number = 0.5;
  public distanceFromCenterNormalized: number = 0;

  constructor(
    camera: THREE.PerspectiveCamera,
    scene: THREE.Scene,
    audioManager: AudioManager
  ) {
    this.camera = camera;
    this.scene = scene;
    this.audioManager = audioManager;

    this.cursorElement = document.getElementById(CURSOR_ICON_ID);
    this.cursorAnimatorElement = null;
    if (this.cursorElement) {
      this.cursorAnimatorElement = this.cursorElement.querySelector(
        `#${CURSOR_ANIMATOR_ID}`
      );
      if (this.cursorAnimatorElement) {
        this.cursorIconElement = this.cursorAnimatorElement.querySelector("i");
      }
      if (!this.cursorIconElement) {
        console.warn(
          "Custom cursor icon (<i> tag) not found inside #cursorAnimator!"
        );
      }
    } else {
      console.warn("Custom cursor element (#cursorIcon) not found!");
    }

    document.addEventListener("mousemove", this.onMouseMove.bind(this));
    document.documentElement.addEventListener(
      "mouseout",
      this.onMouseOut.bind(this)
    );
    document.documentElement.addEventListener(
      "mouseenter",
      this.onMouseEnter.bind(this)
    );

    window.addEventListener("focus", this.onWindowFocus.bind(this));
    window.addEventListener("blur", this.onWindowBlur.bind(this));

    this.updateCustomCursorIcon();
    this.calculateDistanceFromCenter(
      window.innerWidth / 2,
      window.innerHeight / 2
    );
    if (this.cursorElement) {
      this.cursorElement.style.transform = `translate3d(${
        window.innerWidth / 2
      }px, ${window.innerHeight / 2}px, 0)`;
      if (document.hasFocus()) {
        this.onWindowFocus();
      } else {
        this.onWindowBlur();
      }
    }
  }

  private calculateDistanceFromCenter(mouseX: number, mouseY: number): void {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const dx = mouseX - centerX;
    const dy = mouseY - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);
    if (maxDistance > 0) {
      this.distanceFromCenterNormalized = Math.min(distance / maxDistance, 1.0);
    } else {
      this.distanceFromCenterNormalized = 0;
    }
  }

  private onMouseMove(event: MouseEvent): void {
    const windowHalfX = window.innerWidth / 2;
    const windowHalfY = window.innerHeight / 2;
    this.targetMouseX = (event.clientX - windowHalfX) / 100;
    this.targetMouseY = (event.clientY - windowHalfY) / 100;
    this.normalizedMouseX = event.clientX / window.innerWidth;
    this.normalizedMouseY = event.clientY / window.innerHeight;
    this.calculateDistanceFromCenter(event.clientX, event.clientY);
    if (this.cursorElement) {
      this.cursorElement.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0)`;
    }
  }

  private onMouseOut(event: MouseEvent): void {
    if (!event.relatedTarget && document.hasFocus() && this.cursorElement) {
      this.cursorElement.style.display = "none";
    }
  }

  private onMouseEnter(_event: MouseEvent): void {
    if (document.hasFocus() && this.cursorElement) {
      this.cursorElement.style.display = "block";
      this.cursorElement.style.transform = `translate3d(${this.lastKnownMouseX}px, ${this.lastKnownMouseY}px, 0)`;
    }
  }

  private onWindowFocus(): void {
    requestAnimationFrame(() => {
      document.body.style.cursor = "none";
      if (this.cursorElement) {
        this.cursorElement.style.display = "block";
        this.cursorElement.style.transform = `translate3d(${this.lastKnownMouseX}px, ${this.lastKnownMouseY}px, 0)`;
      }
    });
  }

  private onWindowBlur(): void {
    requestAnimationFrame(() => {
      document.body.style.cursor = "none";
      if (this.cursorElement) {
        this.cursorElement.style.display = "none";
      }
    });
  }

  public updateCustomCursorIcon(): void {
    if (!this.cursorIconElement) return;

    if (this.audioManager.isPlaying) {
      this.cursorIconElement.className = `mdi ${ICON_CLASS_MUSIC_NOTE}`;
      if (this.cursorAnimatorElement) {
        this.cursorAnimatorElement.classList.add("no-animation");
      }
    } else {
      this.cursorIconElement.className = `mdi ${ICON_CLASS_PLAY}`;
      if (this.cursorAnimatorElement) {
        this.cursorAnimatorElement.classList.remove("no-animation");
      }
    }
  }

  public updateCamera(): void {
    this.camera.position.x +=
      (this.targetMouseX - this.camera.position.x) * 0.05;
    this.camera.position.y +=
      (-this.targetMouseY - this.camera.position.y) * 0.5;
    this.camera.lookAt(this.scene.position);
  }
}
